import React, { useCallback, useEffect, useState } from 'react';
import karaokeService from '../../services/karaoke.service';
import { formatClock } from './audioEngine';
import ShareTake from './ShareTake';

/**
 * Covers people have recorded. `mine` switches to the caller's own takes,
 * including unlisted and private ones, and turns on the owner controls.
 */
export default function TakeGallery({ mine = false, songId, currentUser }) {
  const [takes, setTakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sharing, setSharing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = mine
        ? await karaokeService.listMyTakes()
        : await karaokeService.listTakes({ song_id: songId });
      setTakes(data.takes || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [mine, songId]);

  useEffect(() => { load(); }, [load]);

  const act = async (take, action) => {
    setBusyId(take.id);
    setError(null);
    try {
      if (action === 'like') {
        const result = await karaokeService.likeTake(take.id);
        setTakes((list) => list.map((t) => (t.id === take.id ? { ...t, likes: result.likes } : t)));
      } else if (action === 'video') {
        const result = await karaokeService.renderVideo(take.id);
        setTakes((list) => list.map((t) => (
          t.id === take.id ? { ...t, video_url: result.video_url, video_status: 'ready' } : t
        )));
      } else if (action === 'share') {
        await karaokeService.shareTake(take.id);
        setTakes((list) => list.map((t) => (t.id === take.id ? { ...t, post_id: -1 } : t)));
      } else if (action === 'delete') {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Delete this take? The audio and video are removed too.')) {
          setBusyId(null);
          return;
        }
        await karaokeService.deleteTake(take.id);
        setTakes((list) => list.filter((t) => t.id !== take.id));
      }
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  };

  if (loading) return <p className="gs-karaoke__empty">Loading…</p>;

  return (
    <section>
      {error && <div className="gs-karaoke__error">{error}</div>}
      {!takes.length && (
        <p className="gs-karaoke__empty">
          {mine ? "You haven't saved a take yet." : 'No covers yet — be the first.'}
        </p>
      )}

      {takes.map((take) => (
        <article key={take.id} className="gs-take">
          <header>
            <div>
              <strong>{take.singer}</strong>
              <small>
                {take.song_title}
                {take.duration_sec ? ` · ${formatClock(take.duration_sec)}` : ''}
                {take.effect ? ` · ${take.effect}` : ''}
                {mine && take.visibility !== 'public' ? ` · ${take.visibility}` : ''}
              </small>
            </div>
            {!mine && (
              <button
                type="button"
                className="gs-take__like"
                disabled={!currentUser || busyId === take.id}
                onClick={() => act(take, 'like')}
              >
                ♥ {take.likes || 0}
              </button>
            )}
          </header>

          <audio className="gs-studio__player" src={take.audio_url} controls preload="none" />

          <div className="gs-take__actions">
            {take.video_url ? (
              <button
                type="button"
                className="gs-btn gs-btn--ghost"
                onClick={() => setSharing(sharing === take.id ? null : take.id)}
              >
                {sharing === take.id ? 'Hide share options' : 'Download / Share'}
              </button>
            ) : mine ? (
              <button
                type="button"
                className="gs-btn gs-btn--ghost"
                disabled={busyId === take.id}
                onClick={() => act(take, 'video')}
              >
                {busyId === take.id ? 'Making video…' : 'Make video'}
              </button>
            ) : null}

            {mine && !take.post_id && (
              <button
                type="button"
                className="gs-btn gs-btn--ghost"
                disabled={busyId === take.id}
                onClick={() => act(take, 'share')}
              >
                Share to feed
              </button>
            )}
            {mine && take.post_id && <span className="gs-take__shared">Shared to feed</span>}

            {mine && (
              <button
                type="button"
                className="gs-btn gs-btn--ghost gs-btn--danger"
                disabled={busyId === take.id}
                onClick={() => act(take, 'delete')}
              >
                Delete
              </button>
            )}
          </div>

          {sharing === take.id && take.video_url && (
            <ShareTake
              videoUrl={take.video_url}
              title={take.title || `${take.song_title} — cover by ${take.singer}`}
              description={
                `${take.song_title} — cover by ${take.singer}\n\n` +
                `Recorded on GeetSuhane — https://geetsuhane.com/fs`
              }
            />
          )}
        </article>
      ))}
    </section>
  );
}
